import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Spinner, Alert, ButtonGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const PhotoGallery = () => {
  const { refreshUser } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingAvatar, setSettingAvatar] = useState(false);
  const [rotatingPhotoId, setRotatingPhotoId] = useState(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await api.get('/photos');

        if (response.data && response.data.data) {
          setPhotos(response.data.data);
        } else {
          // If API doesn't return expected data, use mock data
          setPhotos([
            {
              id: 1,
              status: 'Approved',
              private: false,
              avatar: true,
              denial_reason: null,
              deleted: false,
              small_url: 'https://randomuser.me/api/portraits/women/44.jpg',
              medium_url: 'https://randomuser.me/api/portraits/women/44.jpg',
              large_url: 'https://randomuser.me/api/portraits/women/44.jpg'
            },
            {
              id: 2,
              status: 'Pending',
              private: false,
              avatar: false,
              denial_reason: null,
              deleted: false,
              small_url: 'https://randomuser.me/api/portraits/women/43.jpg',
              medium_url: 'https://randomuser.me/api/portraits/women/43.jpg',
              large_url: 'https://randomuser.me/api/portraits/women/43.jpg'
            },
            {
              id: 3,
              status: 'Approved',
              private: true,
              avatar: false,
              denial_reason: null,
              deleted: false,
              small_url: 'https://randomuser.me/api/portraits/women/42.jpg',
              medium_url: 'https://randomuser.me/api/portraits/women/42.jpg',
              large_url: 'https://randomuser.me/api/portraits/women/42.jpg'
            },
            {
              id: 4,
              status: 'Denied',
              private: false,
              avatar: false,
              denial_reason: 'Photo does not meet our guidelines',
              deleted: false,
              small_url: 'https://randomuser.me/api/portraits/women/41.jpg',
              medium_url: 'https://randomuser.me/api/portraits/women/41.jpg',
              large_url: 'https://randomuser.me/api/portraits/women/41.jpg'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching photos:', error);
        setError('Failed to load photos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPhoto(null);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeleting(true);

    try {
      await api.delete(`/photos/${photoId}`);

      // Remove the photo from the state
      setPhotos(photos.filter(photo => photo.id !== photoId));

      // Close the modal if the deleted photo is the selected one
      if (selectedPhoto && selectedPhoto.id === photoId) {
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSetAvatar = async (photoId) => {
    setSettingAvatar(true);
    setError(null);
    setSuccess(null);

    try {
      await api.patch(`/photos/${photoId}`, { avatar: true });

      // Update the photos state to reflect the new avatar
      setPhotos(photos.map(photo => ({
        ...photo,
        avatar: photo.id === photoId
      })));

      // Refresh the user data to update the profile picture in the header
      await refreshUser();

      // Set success message
      setSuccess('Profile picture updated successfully! Your profile picture has been updated across the site.');

      // Close the modal if it's open
      if (showModal) {
        handleCloseModal();
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Error setting avatar:', error);
      setError('Failed to set profile picture. Please try again.');
    } finally {
      setSettingAvatar(false);
    }
  };

  const handleRotatePhoto = async (photoId, direction) => {
    setRotatingPhotoId(photoId);
    setError(null);
    setSuccess(null);

    try {
      // Check if the photo being rotated is the current avatar
      const isAvatar = photos.find(photo => photo.id === photoId)?.avatar || false;

      // Make API call to rotate the photo
      await api.patch(`/photos/${photoId}`, { rotate: direction });

      // Fetch the updated photos to show the rotated image
      const response = await api.get('/photos');
      if (response.data && response.data.data) {
        setPhotos(response.data.data);

        // If we're rotating the selected photo in the modal, update it too
        if (selectedPhoto && selectedPhoto.id === photoId) {
          const updatedPhoto = response.data.data.find(photo => photo.id === photoId);
          if (updatedPhoto) {
            setSelectedPhoto(updatedPhoto);
          }
        }

        // If the rotated photo is the avatar/profile picture, refresh the user data
        // to update the profile picture in the header
        if (isAvatar) {
          await refreshUser();
          setSuccess(`Photo rotated ${direction === 'left' ? 'counter-clockwise' : 'clockwise'} successfully! Your profile picture has been updated across the site.`);
        } else {
          setSuccess(`Photo rotated ${direction === 'left' ? 'counter-clockwise' : 'clockwise'} successfully!`);
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error rotating photo:', error);
      setError('Failed to rotate photo. Please try again.');
    } finally {
      setRotatingPhotoId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved':
        return <Badge bg="success">Approved</Badge>;
      case 'Pending':
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case 'Denied':
        return <Badge bg="danger">Denied</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading photos...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">My Photos</h2>
        <Button as={Link} to="/photos/upload" variant="primary">
          <i className="bi bi-plus-lg me-2"></i>
          Upload New Photo
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      {photos.length === 0 ? (
        <Card className="border-0 shadow-sm text-center p-5">
          <Card.Body>
            <i className="bi bi-images display-1 text-muted mb-3"></i>
            <h3 className="fw-bold">No Photos Yet</h3>
            <p className="text-muted mb-4">
              Upload photos to enhance your profile and increase your chances of getting matches.
            </p>
            <Button as={Link} to="/photos/upload" variant="primary">
              Upload Your First Photo
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="photo-gallery">
          {photos.map(photo => (
            <div key={photo.id} className="photo-gallery-item">
              <Card className="border-0 shadow-sm h-100">
                <div
                  className="position-relative photo-container"
                  style={{ height: '250px', overflow: 'hidden' }}
                >
                  {rotatingPhotoId === photo.id ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="text-center">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 small">Rotating...</p>
                      </div>
                    </div>
                  ) : (
                    <Card.Img
                      variant="top"
                      src={photo.large_url}
                      alt="Photo"
                      className="photo-image"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePhotoClick(photo)}
                    />
                  )}

                  <div className="position-absolute top-0 end-0 p-2">
                    {getStatusBadge(photo.status)}
                  </div>

                  {photo.avatar && (
                    <div className="position-absolute top-0 start-0 p-2">
                      <Badge bg="primary">
                        <i className="bi bi-person-badge me-1"></i>
                        Profile Picture
                      </Badge>
                    </div>
                  )}

                  {photo.private && (
                    <div className="position-absolute bottom-0 start-0 p-2">
                      <Badge bg="secondary">
                        <i className="bi bi-lock me-1"></i>
                        Private
                      </Badge>
                    </div>
                  )}
                </div>

                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Photo #{photo.id}</small>
                  </div>

                  {photo.status === 'Denied' && photo.denial_reason && (
                    <Alert variant="danger" className="py-1 px-2 mb-2">
                      <small>{photo.denial_reason}</small>
                    </Alert>
                  )}

                  {photo.status === 'Approved' && photo.private && (
                    <Alert variant="info" className="py-1 px-2 mb-2">
                      <small>Private photos cannot be set as profile pictures</small>
                    </Alert>
                  )}

                  <div className="d-flex justify-content-center align-items-center mb-2">
                    <ButtonGroup className="me-2">
                      <Button
                        className="btn-sm-action btn-rotate"
                        onClick={() => handleRotatePhoto(photo.id, 'left')}
                        disabled={rotatingPhotoId !== null}
                        title="Rotate Left"
                      >
                        <i className="bi bi-arrow-counterclockwise"></i>
                      </Button>
                      <Button
                        className="btn-sm-action btn-rotate"
                        onClick={() => handleRotatePhoto(photo.id, 'right')}
                        disabled={rotatingPhotoId !== null}
                        title="Rotate Right"
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </Button>
                    </ButtonGroup>
                    <Button
                      variant="outline-danger"
                      className="btn-sm-action btn-delete-photo"
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deleting}
                      title="Delete Photo"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>

                  {!photo.avatar && photo.status === 'Approved' && !photo.private && (
                    <Button
                      className="btn-feature btn-profile-picture mt-auto"
                      onClick={() => handleSetAvatar(photo.id)}
                      disabled={settingAvatar}
                    >
                      Set as Profile Picture
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Photo Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        dialogClassName="photo-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Photo #{selectedPhoto?.id}
            {selectedPhoto?.status && (
              <span className="ms-2">
                {getStatusBadge(selectedPhoto.status)}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {selectedPhoto && (
            <div className="photo-modal-container">
              {selectedPhoto && rotatingPhotoId === selectedPhoto.id ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <p className="mt-3">Rotating photo...</p>
                  </div>
                </div>
              ) : (
                <img
                  src={selectedPhoto.large_url}
                  alt="Full size"
                  className="img-fluid photo-modal-image"
                  style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {selectedPhoto?.avatar && (
              <Badge bg="primary" className="me-2">
                <i className="bi bi-person-badge me-1"></i>
                Profile Picture
              </Badge>
            )}

            {selectedPhoto?.private && (
              <Badge bg="secondary">
                <i className="bi bi-lock me-1"></i>
                Private
              </Badge>
            )}
          </div>

          <div className="d-flex align-items-center">
            {selectedPhoto && selectedPhoto.status === 'Approved' && selectedPhoto.private && (
              <Alert variant="info" className="py-1 px-2 mb-0 me-2 d-inline-block">
                <small>Private photos cannot be set as profile pictures</small>
              </Alert>
            )}

            {selectedPhoto && !selectedPhoto.avatar && selectedPhoto.status === 'Approved' && !selectedPhoto.private && (
              <Button
                className="btn-feature btn-profile-picture me-2"
                onClick={() => handleSetAvatar(selectedPhoto.id)}
                disabled={settingAvatar}
              >
                {settingAvatar ? 'Setting...' : 'Set as Profile Picture'}
              </Button>
            )}

            {selectedPhoto && (
              <div className="d-flex align-items-center">
                <ButtonGroup className="me-2">
                  <Button
                    className="btn-rotate"
                    onClick={() => handleRotatePhoto(selectedPhoto.id, 'left')}
                    disabled={rotatingPhotoId !== null}
                    title="Rotate Left"
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                    {rotatingPhotoId === selectedPhoto.id ? 'Rotating...' : 'Rotate Left'}
                  </Button>
                  <Button
                    className="btn-rotate"
                    onClick={() => handleRotatePhoto(selectedPhoto.id, 'right')}
                    disabled={rotatingPhotoId !== null}
                    title="Rotate Right"
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    {rotatingPhotoId === selectedPhoto.id ? 'Rotating...' : 'Rotate Right'}
                  </Button>
                </ButtonGroup>
                <Button
                  variant="outline-danger"
                  className="btn-delete-photo"
                  onClick={() => handleDeletePhoto(selectedPhoto.id)}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Photo'}
                </Button>
              </div>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PhotoGallery;
